import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { de, enUS } from 'date-fns/locale';
import UserLanding from 'features/account/user/user-landing';
import CallTranscriptUI from 'features/co-pilot/call-transcripts/call-transcripts';
import LinkedinAccountExternal from 'features/co-pilot/linkedin-accounts/linkedin-account-external';
import GlobalScrollbarStyles from 'features/ui/app-layout/custom-scrollbar';
import PrivateRoute from 'features/ui/app-layout/private-route';
import AdminRoute from 'features/ui/app-layout/admin-route';
import { DynamicLandingRedirect } from 'features/ui/app-layout/feature-gated-route';
import ErrorBoundary from 'features/ui/error-boundary/error-boundary';
import useCheckFrontendVersion from 'hooks/use-check-frontend-version';
import { useProfileLanguage } from 'hooks/use-profile-language';
import { useUserData } from 'hooks/use-user-data';
import i18n from 'i18n';
import AccountManagement from 'pages/account-management';
import Administration from 'pages/admin';
import AdminLinkedinSessionMonitorPage from 'pages/admin-linkedin-session-monitor';
import AdminCampaignContactTimelinePage from 'pages/admin-campaign-contact-timeline';
import AdminFeedbackTicketsPage from 'pages/admin-feedback-tickets';
import AdminEngagementPage from 'pages/admin-engagement';
import NotificationsPage from 'pages/notifications';
import FeedbackTicketPage from 'pages/feedback-ticket';
import RedirectToLogin from 'features/ui/app-layout/redirect-to-login';
import AIChatPage from 'pages/ai-chat';
import AdminMonitoring from 'pages/admin-monitoring';
import AdminQueueManagement from 'pages/admin-queue-management';
import AdminUserOverview from 'pages/admin-user-overview';
import AugmentedWriting from 'pages/augmented-writing';
import AugmentedWritingComplete from 'pages/augmented-writing-complete';
import CoPilotRootLayout from 'pages/co-pilot/co-pilot-root-layout';
import { getCoPilotChildRoutes } from 'pages/co-pilot/co-pilot-route-children';
import ContactDetails from 'pages/contact-details';
import ContactManagement from 'pages/contact-management';
import ExtensionInstallationSuccess from 'pages/extension-installation-success';
import FeatureOverview from 'pages/feature-overview';
import HrBooster from 'pages/hr-booster';
import HrBoosterTextGenerationNew from 'pages/hr-booster-text-generation';
import LeadBooster from 'pages/lead-booster';
import LeadBoosterTextGeneration from 'pages/lead-booster-text-generation';
import LeadListResearchGenerate from 'pages/lead-list-research-generate';
import LeadListStorage from 'pages/lead-list-storage';
import LeadListStorageDetail from 'pages/lead-list-storage-detail';
import LeadManageQuestionnaires from 'pages/lead-manage-questionnaires';
import LeadQuestionnaire from 'pages/lead-questionnaire';
import LeadQuestionnaireDetails from 'pages/lead-questionnaire-details';
import LinkedinProfileFinder from 'pages/linkedin-profile-finder';
import MotivEManageSurveys from 'pages/motive-manage-surveys';
import MotiveSurvey from 'pages/motive-survey';
import MotiveTests from 'pages/motive-tests';
import MotiveWebReportPage from 'pages/motive-web-report';
import PageNotFound from 'pages/page-not-found';
import Payment from 'pages/payment';
import Pipeline from 'pages/pipeline';
import ProfileGenerator from 'pages/profile-generator';
import PromptManagement from 'pages/prompt-management';
import ResetPasswordEnterNew from 'pages/reset-password-enter-new';
import ResetPasswordRequest from 'pages/reset-password-request';
import SignUp from 'pages/sign-up';
import SignUpSuccess from 'pages/sign-up-success';
import StatusMessageManagement from 'pages/status-message-management';
import SudoSignIn from 'pages/sudo-sign-in';
import UseCaseStore from 'pages/use-case-store';
import React, { useEffect } from 'react';
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router';
import { ReactFlowProvider } from 'reactflow';
import theme from 'theme';
import { TssCacheProvider } from 'tss-react';
import config from './config/default';
import { ProductGroups, ProfileTypes, Routes as RoutesEnum } from './enums';
import WebmeticsLanding from './pages/apps-management'; // adjust the import path as needed
import OauthSuccess from './pages/o-auth-success';
import PublicChatRoute from './pages/public-chat';

const muiCache = createCache({
  key: 'mui',
  prepend: true,
});

const tssCache = createCache({
  key: 'tss',
});

const stripePromise = loadStripe(config.stripe_pk);

declare global {
  interface Window {
    _rwq: any;
    Rewardful: any;
  }
}

const App: React.FC = () => {
  const { isAuthenticated } = useUserData();
  const authenticated = isAuthenticated();

  const queryParams = new URLSearchParams(window.location.search);
  const email = queryParams.get('optOutEmail');

  const adapterLocale = i18n.languages[0] === 'de' ? de : enUS;

  useCheckFrontendVersion();

  // Session-Restore: UI-Sprache aus dem Nutzerprofil übernehmen.
  useProfileLanguage();

  useEffect(() => {
    (function (w: any, r: any) {
      w._rwq = r;
      w[r] =
        w[r] ||
        function (...args: any[]) {
          (w[r].q = w[r].q || []).push(args);
        };
    })(window, 'rewardful');
    const scriptOne = document.createElement('script');
    scriptOne.async = true;
    scriptOne.src = 'https://r.wdfl.co/rw.js';
    scriptOne.setAttribute('data-rewardful', 'dcceb5');

    document.head.appendChild(scriptOne);
  }, []);

  const router = createBrowserRouter(
    [
      {
        path: '/',
        element: authenticated ? (
          <PrivateRoute>
            <DynamicLandingRedirect />
          </PrivateRoute>
        ) : (
          <ErrorBoundary>
            <PublicChatRoute />
          </ErrorBoundary>
        ),
      },
      {
        path: RoutesEnum.PUBLIC_CHAT,
        element: (
          <ErrorBoundary>
            <PublicChatRoute />
          </ErrorBoundary>
        ),
      },
      {
        path: '/blank',
        element: (
          <PrivateRoute>
            <div />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.LANDING,
        element: (
          <Navigate
            to={{
              pathname: !authenticated ? RoutesEnum.PUBLIC_CHAT : RoutesEnum.AI_CHAT,
              search: email ? `?optOutEmail=${email}` : '',
            }}
            replace={false}
          />
        ),
      },
      {
        path: RoutesEnum.SIGNIN,
        element: <RedirectToLogin />,
      },
      {
        path: RoutesEnum.ADMIN_SIGN_IN,
        element: (
          <ErrorBoundary>
            <UserLanding authenticated={authenticated}>
              <SudoSignIn />
            </UserLanding>
          </ErrorBoundary>
        ),
      },
      {
        path: RoutesEnum.SIGNUP,
        element: (
          <ErrorBoundary>
            <UserLanding authenticated={authenticated}>
              <SignUp />
            </UserLanding>
          </ErrorBoundary>
        ),
      },
      {
        path: RoutesEnum.SIGNUP_SUCCESS,
        element: (
          <ErrorBoundary>
            <UserLanding authenticated={authenticated}>
              <SignUpSuccess />
            </UserLanding>
          </ErrorBoundary>
        ),
      },
      {
        path: RoutesEnum.FORGET_PASSWORD,
        element: (
          <ErrorBoundary>
            <UserLanding authenticated={authenticated}>
              <ResetPasswordRequest />
            </UserLanding>
          </ErrorBoundary>
        ),
      },
      {
        path: RoutesEnum.RESET_PASSWORD,
        element: (
          <ErrorBoundary>
            <UserLanding authenticated={authenticated}>
              <ResetPasswordEnterNew />
            </UserLanding>
          </ErrorBoundary>
        ),
      },
      {
        path: RoutesEnum.INSTALL_EXTENSION_SUCCESS,
        element: (
          <ErrorBoundary>
            <ExtensionInstallationSuccess />
          </ErrorBoundary>
        ),
      },
      {
        path: `${RoutesEnum.SURVEY}/:id`,
        element: (
          <ErrorBoundary>
            <MotiveSurvey />
          </ErrorBoundary>
        ),
      },
      {
        path: `${RoutesEnum.MOTIVE_WEB_REPORT}/:token`,
        element: (
          <ErrorBoundary>
            <MotiveWebReportPage />
          </ErrorBoundary>
        ),
      },
      {
        path: RoutesEnum.SURVEY_OVERVIEW,
        element: (
          <PrivateRoute>
            <MotivEManageSurveys />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.LEAD_CREATION,
        element: (
          <PrivateRoute>
            <LeadManageQuestionnaires />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.LEAD_CREATION_DETAILS,
        element: (
          <PrivateRoute>
            <LeadQuestionnaireDetails />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.LEAD_QUESTIONNAIRE,
        element: (
          <ErrorBoundary>
            <LeadQuestionnaire />
          </ErrorBoundary>
        ),
      },
      {
        path: RoutesEnum.OAUTH_MICROSOFT_SUCCESS,
        element: (
          <ErrorBoundary>
            <OauthSuccess />
          </ErrorBoundary>
        ),
      },
      {
        path: RoutesEnum.OAUTH_GMAIL_SUCCESS,
        element: (
          <ErrorBoundary>
            <OauthSuccess />
          </ErrorBoundary>
        ),
      },
      {
        path: RoutesEnum.ADMINISTRATION,
        element: (
          <AdminRoute>
            <Administration />
          </AdminRoute>
        ),
      },
      {
        path: RoutesEnum.ADMIN_MONITORING,
        element: (
          <AdminRoute>
            <AdminMonitoring />
          </AdminRoute>
        ),
      },
      {
        path: RoutesEnum.ADMIN_USER_OVERVIEW,
        element: (
          <AdminRoute>
            <AdminUserOverview />
          </AdminRoute>
        ),
      },
      {
        path: RoutesEnum.ADMIN_QUEUE_MANAGEMENT,
        element: (
          <AdminRoute>
            <AdminQueueManagement />
          </AdminRoute>
        ),
      },
      {
        path: RoutesEnum.ADMIN_LINKEDIN_SESSION_MONITOR,
        element: (
          <AdminRoute>
            <AdminLinkedinSessionMonitorPage />
          </AdminRoute>
        ),
      },
      {
        path: RoutesEnum.ADMIN_CAMPAIGN_CONTACT_TIMELINE,
        element: (
          <AdminRoute>
            <AdminCampaignContactTimelinePage />
          </AdminRoute>
        ),
      },
      {
        path: RoutesEnum.ADMIN_FEEDBACK_TICKETS,
        element: (
          <AdminRoute>
            <AdminFeedbackTicketsPage />
          </AdminRoute>
        ),
      },
      {
        path: `${RoutesEnum.ADMIN_ENGAGEMENT}/:tab?`,
        element: (
          <AdminRoute>
            <AdminEngagementPage />
          </AdminRoute>
        ),
      },
      {
        path: RoutesEnum.NOTIFICATIONS,
        element: (
          <PrivateRoute>
            <NotificationsPage />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.PROMPT_MANAGEMENT,
        element: (
          <AdminRoute>
            <PromptManagement />
          </AdminRoute>
        ),
      },
      {
        path: RoutesEnum.STATUS_MESSAGE_MANAGEMENT,
        element: (
          <AdminRoute>
            <StatusMessageManagement />
          </AdminRoute>
        ),
      },
      {
        path: RoutesEnum.FEATURE_OVERVIEW,
        element: (
          <PrivateRoute>
            <FeatureOverview />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.HR_BOOSTER,
        element: (
          <PrivateRoute>
            <HrBooster />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.HR_BOOSTER_GENERATE_GLOBAL_PROFILE,
        element: (
          <PrivateRoute>
            <HrBoosterTextGenerationNew profileType={ProfileTypes.GLOBAL} />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.HR_BOOSTER_GENERATE_TAILORED_PROFILE,
        element: (
          <PrivateRoute>
            <HrBoosterTextGenerationNew profileType={ProfileTypes.TAILORED} />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.HR_BOOSTER_OPTIMIZE,
        element: (
          <PrivateRoute>
            <AugmentedWriting />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.HR_BOOSTER_DONE,
        element: (
          <PrivateRoute>
            <AugmentedWritingComplete />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.HR_BOOSTER_AUTO_FUNNEL,
        element: <Navigate to={{ pathname: RoutesEnum.RECRUITING_BOOSTER_CO_PILOT_PROJECTS }} />,
      },
      {
        path: RoutesEnum.LEAD_BOOSTER_AUTO_FUNNEL,
        element: <Navigate to={{ pathname: RoutesEnum.LEAD_BOOSTER_CO_PILOT_PROJECTS }} />,
      },
      {
        path: RoutesEnum.EXTERNAL_LINKEDIN_ACCOUNTS,
        element: (
          <ErrorBoundary>
            <LinkedinAccountExternal />
          </ErrorBoundary>
        ),
      },
      {
        path: RoutesEnum.EXTERNAL_CALL_TRANSCRIPTS,
        element: (
          <PrivateRoute>
            <CallTranscriptUI />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.LEAD_BOOSTER,
        element: (
          <PrivateRoute>
            <LeadBooster />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.LEAD_BOOSTER_GENERATE_GLOBAL_PROFILE,
        element: (
          <PrivateRoute>
            <LeadBoosterTextGeneration profileType={ProfileTypes.GLOBAL} />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.LEAD_BOOSTER_GENERATE_TAILORED_PROFILE,
        element: (
          <PrivateRoute>
            <LeadBoosterTextGeneration profileType={ProfileTypes.TAILORED} />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.LEAD_BOOSTER_OPTIMIZE,
        element: (
          <PrivateRoute>
            <AugmentedWriting />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.LEAD_BOOSTER_DONE,
        element: (
          <PrivateRoute>
            <AugmentedWritingComplete />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.PERSONA_GENERATOR,
        element: (
          <PrivateRoute>
            <ProfileGenerator />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.URL_FINDER,
        element: (
          <PrivateRoute>
            <LinkedinProfileFinder />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.MOTIVE_TESTS,
        element: (
          <PrivateRoute>
            <MotiveTests />
          </PrivateRoute>
        ),
      },
      {
        // Optionaler Reiter (/account/users …): eine Route, damit der Wechsel die Seite nicht neu aufbaut.
        path: `${RoutesEnum.ACCOUNT}/:tab?`,
        element: (
          <PrivateRoute>
            <AccountManagement />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.UPGRADE,
        element: (
          <PrivateRoute>
            <Payment />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.CONTACT_MANAGEMENT_OVERVIEW,
        element: (
          <PrivateRoute>
            <ContactManagement />
          </PrivateRoute>
        ),
      },
      {
        path: `${RoutesEnum.CONTACT_MANAGEMENT_CONTACT}/:contactId`,
        element: (
          <PrivateRoute>
            <ContactDetails />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.RECRUITING_BOOSTER_CO_PILOT,
        element: (
          <PrivateRoute>
            <CoPilotRootLayout productGroup={ProductGroups.HR_BOOSTER} />
          </PrivateRoute>
        ),
        children: getCoPilotChildRoutes(ProductGroups.HR_BOOSTER),
      },
      {
        path: RoutesEnum.LEAD_BOOSTER_CO_PILOT,
        element: (
          <PrivateRoute>
            <CoPilotRootLayout productGroup={ProductGroups.LEAD_BOOSTER} />
          </PrivateRoute>
        ),
        children: getCoPilotChildRoutes(ProductGroups.LEAD_BOOSTER),
      },
      {
        path: '/webmetics', // or use RoutesEnum.WEBMETICS if you've defined it in your enums file
        element: (
          <PrivateRoute>
            <WebmeticsLanding />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.LEAD_LIST_RESEARCH,
        element: (
          <AdminRoute>
            <LeadListResearchGenerate />
          </AdminRoute>
        ),
      },
      {
        path: RoutesEnum.LEAD_LIST_STORAGE,
        element: (
          <AdminRoute>
            <LeadListStorage />
          </AdminRoute>
        ),
      },
      {
        path: `${RoutesEnum.LEAD_LIST_STORAGE}/:id`,
        element: (
          <AdminRoute>
            <LeadListStorageDetail />
          </AdminRoute>
        ),
      },
      {
        path: '/use-case-store',
        element: (
          <AdminRoute>
            <UseCaseStore />
          </AdminRoute>
        ),
      },
      {
        path: RoutesEnum.PIPELINE,
        element: (
          <PrivateRoute>
            <Pipeline />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.AI_CHAT,
        element: (
          <PrivateRoute>
            <AIChatPage />
          </PrivateRoute>
        ),
      },
      {
        path: RoutesEnum.FEEDBACK_TICKET,
        element: (
          <PrivateRoute>
            <FeedbackTicketPage />
          </PrivateRoute>
        ),
      },
      {
        path: '*',
        element: authenticated ? (
          <PrivateRoute>
            <PageNotFound />
          </PrivateRoute>
        ) : (
          // Remember the destination instead of dropping it: an unknown path here is often a
          // perfectly valid one the visitor simply is not logged in for yet.
          <RedirectToLogin />
        ),
      },
    ],
    {
      basename: '/dashboard',
    }
  );

  return (
    <Elements stripe={stripePromise}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>
        <CacheProvider value={muiCache}>
          <TssCacheProvider value={tssCache}>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <GlobalScrollbarStyles />
              <ReactFlowProvider>
                <RouterProvider router={router} />
              </ReactFlowProvider>
            </ThemeProvider>
          </TssCacheProvider>
        </CacheProvider>
      </LocalizationProvider>
    </Elements>
  );
};

export default App;
